import React, { useEffect } from "react";
import {
  BadgeContainer,
  TicketHolder,
  TicketHolderContainer,
  TicketSummaryPanelContainer,
  TicketSummaryPanelContentContainer,
} from "./TicketSummaryPanel.styles";
import {
  MeshGradient,
  MeshColours,
  SessionDetails,
  TicketIcon,
  Badge,
  generateSessionDetails,
  StatusTag,
  BadgeStylesType,
  getTicketType,
} from "..";
import {
  SessionInterface,
  SessionDetailsInterface,
  BookingInterestSessionInterface,
} from "../../models/Session";
import { BookingInterestStatus } from "../../../enums/BookingInterestStatus";
import { TicketInterface } from "../../models/Ticket";
import { Box, Tooltip, useTheme } from "@mui/material";
import { Icon } from "src/icons";
import {
  BookingInterestAllocationInterface,
  BookingInterestUser,
  UserBookingInterest,
  UserBookingInterestsInterface,
} from "src/models/BookingInterest";
import {
  ImageContainer,
} from "../TicketOfferCard/TicketOfferCard.styles";
import { ButtonSwitch } from "../ButtonSwitch/ButtonSwitch";
import { ColumnContainer } from "../Wizard/CreateAllocationWizard/CreateAllocationWizard.styles";
import { GenericTypography } from "src/pages/Scan/Session/components/CommonStyles.styles";
import { validationBookingInterestsFilter } from "src/utils/bookingInterestUtils";
import { AllocationTypeEnum } from "src/models/Allocation";

export interface TicketDetails {
  id: number;
  user: {
    firstName: string;
    lastName: string;
    email?: string;
  };
  assignedFirstName: string;
  assignedLastName: string;
  ticket?: TicketInterface | null;
  isLeadBooker?: boolean;
  bookingInterestStatus?: keyof typeof BookingInterestStatus;
  accessibility?: string,
  otherAccessibility?: string,
  readonly hasAccessibilityRequirements: boolean,
  isChildBooking?: boolean,
  isPriorityTicket?: boolean,
  accessibilityRequestStatus: "Accepted" | "Pending" | "Declined" | undefined;
  ticketIconStyle?: "dotted" | "outlined" | "gold-outlined";
  isParentOrSibling?: boolean;
  userAgeAtBooking?: number;
  /**
   * Consolidated flow: a guest travelling on the lead booker's ticket - no email, no login.
   * Dependents move with the lead booker; an adult invitee is asked directly and answers for
   * themselves, which is why the two get different lines on a booking card.
   */
  isDependent?: boolean;
  /**
   * Consolidated flow: whether this person has an account of their own. The last min list only
   * admits registered accounts and passes over a whole booking that has somebody unregistered
   * on it (LastMinQueueEligibility, docs/consolidated-flow.md §6.6), so a card that does not
   * carry this cannot tell a guest who is in the running from one who is not.
   */
  isRegistered?: boolean;
}

export type TicketsActionType = {
  id: number;
  type: "accepted" | "declined";
};

export interface CustomTicketSummaryPanelProps {
  variant?: "default" | "allocationsAttendance";
  showName?: string | null;
  showMinimumAge?: number | null;
  session?: SessionInterface | BookingInterestSessionInterface | null;
  ticketHolders?: TicketDetails[];
  isDeletable?: boolean;
  colour?: MeshColours;
  handleSubTicketAcceptanceChanges?: Function;
  allocation?: BookingInterestAllocationInterface;
  bookingInterest?: UserBookingInterest;
  currentUser?: BookingInterestUser;
  relatedBookingInterests?: UserBookingInterestsInterface | undefined;
  parentAndSiblingInterests?: UserBookingInterestsInterface | undefined;
  allocatedTicketAccepted?: TicketsActionType[];
  buttonValidation?: boolean;
  disableButtons?: boolean;
  showQrCodeImage?: boolean;
  showTicketHolders?: boolean;
}

interface UserBookingStatusSwitchProps {
  bookingInterestStatus: keyof typeof BookingInterestStatus;
  isDependent: boolean,
  children?: React.ReactNode;
}

const UserBookingStatusSwitch: React.FC<UserBookingStatusSwitchProps> = ({ bookingInterestStatus, isDependent, children }) => {
  switch (bookingInterestStatus) {
    case "EnteredSession":
      return <StatusTag variant="attended" />;
    case "UnattendedSession":
      return <StatusTag variant="noShow" />;
    case "TurnedAway":
      return <StatusTag variant="turnedAway" />;
    case "TicketDeclined":
      return <StatusTag variant="declined" />;
    case "AttendanceDeclinedViaSMS":
      return <StatusTag variant="checkInDeclined" />;
    case "AttendanceConfirmationTimedOutViaSMS":
      return <StatusTag variant="timedoutSms" />;
    case "TicketTimedOut":
      return <StatusTag variant="timedout" />;
    case "TicketAccepted":
      if (!isDependent) {
        return <StatusTag variant="ticketAccepted" />;
      }
    case "AttendanceConfirmedViaSMS":
      if (!isDependent) {
        return <StatusTag variant="checkedIn" />;
      }
    case "AttendanceSMSReminderSent":
      if (!isDependent) {
        return <StatusTag variant="checkInPending" />;
      }
    case "InvitedToTicket":
      if (!isDependent) {
        return <StatusTag variant="awaitingResponse" />;
      }
      return <>{children}</>;
    case "TicketOrInterestRevoked":
      return <StatusTag variant="ticketOrInterestRevoked" />;

    // Consolidated flow (TTT-688): where this guest stands on the last min list.
    //
    // These four used to fall through to `default: null`, so an adult guest's row on the
    // accept page was blank — no buttons (they answer their own invitation) and no status
    // either. A lead booker deciding about their own ticket could not tell from this screen
    // whether their guest had joined the list, was still waiting, or had handed their place
    // back. A dependent never has a queue state of their own: they move with the lead.
    case "JoinedLastMinQueue":
      return isDependent ? null : <StatusTag variant="lastMinQueue" />;
    case "RejoinedQueue":
      return isDependent ? null : <StatusTag variant="rejoinedQueue" />;
    case "SecondChanceExpired":
      return isDependent ? null : <StatusTag variant="secondChanceExpired" />;
    case "InterestRegistered":
    case "UserInterestConfirmedViaEmail":
      return isDependent ? null : <StatusTag variant="applicationPending" />;

    default:
      return null;
  }
};

export const TicketSummaryPanel = ({
  variant = "default",
  showName,
  session,
  ticketHolders,
  isDeletable = false,
  colour = "pink-purple-green",
  showMinimumAge,
  allocation,
  currentUser,
  relatedBookingInterests,
  parentAndSiblingInterests,
  handleSubTicketAcceptanceChanges,
  allocatedTicketAccepted,
  bookingInterest,
  buttonValidation,
  disableButtons = false,
  showQrCodeImage = false,
  showTicketHolders = true,
  ...rest
}: CustomTicketSummaryPanelProps) => {
  const [sessionDetails, setSessionDetails] =
    React.useState<SessionDetailsInterface>({
      name: "",
      date: "",
      arrivalTime: "",
      approxFinishTime: "",
      location: null,
    });
  const [leadBooker, setLeadBooker] =
    React.useState<BookingInterestUser | undefined>(undefined);
  const theme = useTheme();

  useEffect(() => {
    if (bookingInterest?.parentId && parentAndSiblingInterests) {
      parentAndSiblingInterests.forEach((parentAndSiblingInterest) => {
        if (!parentAndSiblingInterest.parentId) {
          setLeadBooker(parentAndSiblingInterest.user);
        }
      })
    } else {
      setLeadBooker(currentUser);
    }
  }, [currentUser, bookingInterest])

  useEffect(() => {
    if (session) {
      const sessionDetailsObj = generateSessionDetails(session);
      setSessionDetails(sessionDetailsObj);
    }
  }, [session]);

  const filteredRelatedBookingInterests = relatedBookingInterests?.filter(validationBookingInterestsFilter)

  function getLeadbookerName(userFirstName: string, userLastName?: string) {
    if (bookingInterest && bookingInterest.assignedFirstName && bookingInterest.assignedLastName) {
      return (
        bookingInterest.assignedFirstName +
        " " +
        bookingInterest.assignedLastName
      );
    } else if (userFirstName?.length > 0) {
      return userFirstName + (userLastName ? " " + userLastName : "");
    } else {
      return "Lead Booker";
    }
  }

  const localHandleSubTicketAcceptanceChanges = async (id: number, type: string) => {
    if (handleSubTicketAcceptanceChanges) {
      await handleSubTicketAcceptanceChanges(id, type);
    }
  };

  const declineLeadBookerAndAllGuests = () => {
    if (handleSubTicketAcceptanceChanges) {
      handleSubTicketAcceptanceChanges(bookingInterest?.id, "declined");

      filteredRelatedBookingInterests?.forEach((relatedBooking) => {
        handleSubTicketAcceptanceChanges(relatedBooking.id, "declined");
      });
    }
  };

  const acceptGuestAndLeadBooker = (id: number) => {
    if (handleSubTicketAcceptanceChanges) {
      handleSubTicketAcceptanceChanges(id, "accepted");

      //Accept lead booker
      handleSubTicketAcceptanceChanges(bookingInterest?.id, "accepted");
    }
  };

  const ticketType = getTicketType(ticketHolders!, allocation?.type);

  const allocatedTicketAcceptedLeadBooker = allocatedTicketAccepted?.find(
    (ticket) => ticket.id === bookingInterest?.id
  );
  return (
    <TicketSummaryPanelContainer
      data-cy="ticketSummaryPanelContainer"
      {...rest}
    >
      <TicketSummaryPanelContentContainer>
        <SessionDetails
          showName={showName}
          showMinimumAge={showMinimumAge}
          sessionDetails={sessionDetails}
          ticketType={ticketHolders ? ticketType : undefined}
          allocation={allocation}
          bookingInterest={bookingInterest}
          isPriority={ticketType === "priority" || ticketType === "priorityAccessibility"}
        />
        {ticketHolders && showTicketHolders &&
          ticketHolders.map((ticketHolder, index) => {
            return (
              <TicketHolderContainer key={`ticket-holder-${index}`}>
                <TicketIcon
                  ticketNumber={index + 1}
                  ticketIconStyle={
                    ticketHolder.ticketIconStyle
                      ? ticketHolder.ticketIconStyle
                      : "dotted"
                  }
                />
                <TicketHolder>
                  {ticketHolder.assignedFirstName || ticketHolder.user.firstName} {ticketHolder.assignedLastName || ticketHolder.user.lastName}
                </TicketHolder>
                <BadgeContainer>
                  {ticketHolder.isLeadBooker && (
                    <Badge badgeStyle="leadBooker" />
                  )}
                  {!!ticketHolder.userAgeAtBooking && ticketHolder.userAgeAtBooking < 18 && <Badge badgeStyle="underAge" />}
                </BadgeContainer>

                <Box sx={{ display: "flex", gap: "4px" }}>
                  {ticketHolder.hasAccessibilityRequirements ? (
                    <Badge badgeStyle="accessibilityRequired" size="sm" />
                  ) : ticketHolder.isPriorityTicket ? (
                    <Badge badgeStyle="prioritySparkles" withoutCircle size="sm" />
                  ) : (
                    <Badge badgeStyle="generalAdmission" size="sm" />
                  )}
                </Box>
              </TicketHolderContainer>
            );
          })}

        {currentUser && (
          <TicketHolderContainer allocationsAttendanceStyle>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                width: "100%",
              }}
            >
              <Box sx={{ display: "flex", gap: "8px" }}>
                <TicketIcon ticketNumber={1} ticketIconStyle={ticketType === "priority" ? "gold-outlined" : "dotted"} />
                <Tooltip
                  title={`${getLeadbookerName(
                    currentUser?.firstName,
                    currentUser?.lastName
                  )}`}
                  placement="top"
                >
                  <TicketHolder>
                    {getLeadbookerName(
                      currentUser?.firstName,
                      currentUser?.lastName
                    )}
                  </TicketHolder>
                </Tooltip>

                <BadgeContainer>
                  <Box sx={{ display: "flex", gap: "8px" }}>
                    {currentUser.id === leadBooker?.id &&
                      <Badge badgeStyle="leadBooker" />
                    }

                    {currentUser?.accessibility && (
                      <Badge badgeStyle="accessibilityRequired" size="sm" />
                    )}

                    {allocation && allocation.type !== 'Priority' ? (
                      <Badge
                        badgeStyle={AllocationTypeEnum[allocation.type] as BadgeStylesType}
                        size="sm"
                      />
                    ) : !allocation ? (
                      <Badge badgeStyle="generalAdmission" size="sm" />
                    ) : null}
                  </Box>
                </BadgeContainer>
              </Box>

              <ButtonSwitch
                value={allocatedTicketAcceptedLeadBooker ? allocatedTicketAcceptedLeadBooker.type === "accepted" : undefined}
                error={buttonValidation && typeof allocatedTicketAcceptedLeadBooker === "undefined"}
                disabled={disableButtons}
                variant="secondary"
                size="sm"
                data-cy={`choice-${1}-accept`}
                leftButtonChildren={
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    <Icon icon="Check" size="sm" /> Accept{" "}
                  </Box>
                }
                rightButtonChildren={
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    <Icon icon="X" size="sm" /> Decline{" "}
                  </Box>
                }
                onLeftButtonClick={() =>
                  localHandleSubTicketAcceptanceChanges(
                    Number(bookingInterest?.id),
                    "accepted"
                  )
                }
                onRightButtonClick={() => declineLeadBookerAndAllGuests()}
              />
            </Box>
          </TicketHolderContainer>
        )}

        {relatedBookingInterests?.map(
          (relatedInterest: UserBookingInterest, index: number) => {
            const allocatedTicketAcceptedElement = allocatedTicketAccepted?.find(
              (ticket) => ticket.id === relatedInterest?.id
            );

            return (
              <TicketHolderContainer
                key={relatedInterest.id}
                allocationsAttendanceStyle
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    gap: "8px",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", gap: "8px", width: "100%", justifyContent: "space-between" }}>
                    {relatedInterest.isDependent && (
                      <Icon icon="ArrowRight" rotate={45} size="sm" />
                    )}
                    <TicketIcon
                      ticketNumber={index + 2}
                      ticketIconStyle={ticketType === "priority" ? "gold-outlined" : "dotted"}
                    />
                    <ColumnContainer>
                      {relatedInterest.isDependent && (
                        <GenericTypography
                          variant="caption"
                          style={{ color: theme.colors["neutral.400"] }}
                        >
                          Dependant of: {bookingInterest?.assignedFirstName || leadBooker?.firstName}{" "}
                          {bookingInterest?.assignedLastName || leadBooker?.lastName}
                        </GenericTypography>
                      )}
                      <Tooltip
                        title={`${relatedInterest.assignedFirstName || relatedInterest.user.firstName} ${relatedInterest.assignedLastName || relatedInterest.user.lastName}`}
                        placement="top"
                      >
                        <TicketHolder>
                          {relatedInterest.assignedFirstName || relatedInterest.user.firstName}{" "}
                          {relatedInterest.assignedLastName || relatedInterest.user.lastName}
                        </TicketHolder>
                      </Tooltip>
                    </ColumnContainer>

                    <BadgeContainer>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        {typeof relatedInterest.user.age !== "undefined" &&
                          relatedInterest.user.age > 0 &&
                          relatedInterest.user.age < 18 && (
                            <Badge badgeStyle="underAge" size="sm" />
                          )}

                        {relatedInterest?.user?.accessibility && (
                          <Badge badgeStyle="accessibilityRequired" size="sm" />
                        )}

                        {/* Consolidated flow: an adult guest who never finished signing up.
                            Same badge, same tooltip as the booking card in the profile - the
                            lead booker is deciding about this person's ticket on this screen,
                            so it has to be answerable here too. */}
                        {relatedInterest?.user?.isRegistered === false && !relatedInterest.isDependent && (
                          <Badge badgeStyle="noAccount" size="sm" />
                        )}

                        {allocation && allocation.type !== 'Priority' ? (
                          <Badge
                            badgeStyle={AllocationTypeEnum[allocation?.type ?? 'Generic'] as BadgeStylesType}
                            size="sm"
                          />
                        ) : !allocation ? (
                          <Badge badgeStyle="generalAdmission" size="sm" />
                        ) : null}
                      </Box>
                    </BadgeContainer>
                  </Box>

                  <UserBookingStatusSwitch bookingInterestStatus={relatedInterest?.status!} isDependent={relatedInterest?.isDependent!}>
                    <ButtonSwitch
                      value={allocatedTicketAcceptedElement ? allocatedTicketAcceptedElement.type === "accepted" : undefined}
                      variant="secondary"
                      size="sm"
                      error={buttonValidation && typeof allocatedTicketAcceptedElement === "undefined"}
                      disabled={disableButtons}
                      data-cy={`choice-${index}-accept`}
                      leftButtonChildren={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                        >
                          <Icon icon="Check" size="sm" /> Accept{" "}
                        </Box>
                      }
                      rightButtonChildren={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                        >
                          <Icon icon="X" size="sm" /> Decline{" "}
                        </Box>
                      }
                      onLeftButtonClick={() =>
                        acceptGuestAndLeadBooker(Number(relatedInterest?.id))
                      }
                      onRightButtonClick={() =>
                        localHandleSubTicketAcceptanceChanges(
                          Number(relatedInterest?.id),
                          "declined"
                        )
                      }
                    />
                  </UserBookingStatusSwitch>

                </Box>
              </TicketHolderContainer>
            )
          }
        )}

        {relatedBookingInterests?.length === 0 && parentAndSiblingInterests?.map(
          (parentAndSiblingInterest: UserBookingInterest, index: number) => {
            return (
              <TicketHolderContainer
                key={parentAndSiblingInterest.id}
                allocationsAttendanceStyle
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    gap: "8px",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", gap: "8px", width: "100%", justifyContent: "space-between" }}>
                    {parentAndSiblingInterest.isDependent && (
                      <Icon icon="ArrowRight" rotate={45} size="sm" />
                    )}

                    <TicketIcon
                      ticketNumber={index + 2}
                      ticketIconStyle={ticketType === "priority" ? "gold-outlined" : "dotted"}
                    />
                    <ColumnContainer>
                      {parentAndSiblingInterest.isDependent && (
                        <GenericTypography
                          variant="caption"
                          style={{ color: theme.colors["neutral.400"] }}
                        >
                          Dependant of: {bookingInterest?.assignedFirstName || leadBooker?.firstName}{" "}
                          {bookingInterest?.assignedLastName || leadBooker?.lastName}
                        </GenericTypography>
                      )}
                      <Tooltip
                        title={`${parentAndSiblingInterest.assignedFirstName || parentAndSiblingInterest.user.firstName} ${parentAndSiblingInterest.assignedLastName || parentAndSiblingInterest.user.lastName}`}
                        placement="top"
                      >
                        <TicketHolder>
                          {parentAndSiblingInterest.assignedFirstName || parentAndSiblingInterest.user.firstName}{" "}
                          {parentAndSiblingInterest.assignedLastName || parentAndSiblingInterest.user.lastName}
                        </TicketHolder>
                      </Tooltip>
                    </ColumnContainer>

                    <BadgeContainer>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        {parentAndSiblingInterest.user.id === leadBooker?.id &&
                          <Badge badgeStyle="leadBooker" />
                        }

                        {typeof parentAndSiblingInterest.user.age !== "undefined" &&
                          parentAndSiblingInterest.user.age > 0 &&
                          parentAndSiblingInterest.user.age < 18 && (
                            <Badge badgeStyle="underAge" size="sm" />
                          )}

                        {parentAndSiblingInterest?.user?.accessibility && (
                          <Badge badgeStyle="accessibilityRequired" size="sm" />
                        )}

                        {allocation && allocation.type !== 'Priority' ? (
                          <Badge
                            badgeStyle={AllocationTypeEnum[allocation?.type ?? 'Generic'] as BadgeStylesType}
                            size="sm"
                          />
                        ) : !allocation ? (
                          <Badge badgeStyle="generalAdmission" size="sm" />
                        ) : null}
                      </Box>
                    </BadgeContainer>
                  </Box>

                  <UserBookingStatusSwitch bookingInterestStatus={parentAndSiblingInterest?.status!} isDependent={parentAndSiblingInterest?.isDependent!} />

                </Box>
              </TicketHolderContainer>
            )
          }
        )}
      </TicketSummaryPanelContentContainer>
      <MeshGradient
        colour={(ticketType === "priority" || ticketType === "priorityAccessibility") ? "light-gold" : colour}
        position="bottom"
        size="low"
        sx={{ top: 0 }}
      />
    </TicketSummaryPanelContainer>
  );
};

export interface TicketSummaryPanelProps
  extends React.ComponentProps<typeof TicketSummaryPanel> {
  as?: React.ElementType;
}

TicketSummaryPanel.displayName = "TicketSummaryPanel";
